import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, ExternalLink, Globe, Loader2, Plug, RefreshCw, Terminal as TerminalIcon, X } from 'lucide-react';
import { useSessionStore } from '@/lib/store/sessionStore';
import { useUIStore } from '@/lib/store/uiStore';

type ProviderId = 'claude' | 'codex' | 'gemini';

const PROVIDERS: Array<{ id: ProviderId; label: string }> = [
  { id: 'claude', label: 'Claude (CLI)' },
  { id: 'codex',  label: 'Codex (CLI)' },
  { id: 'gemini', label: 'Gemini (CLI)' },
];

/**
 * Resolve the Loom API base URL — same convention as BackendCliAdapter:
 * use ?host= when iframed cross-origin, otherwise relative.
 */
function loomApi(): string {
  const RELATIVE = '/api/v1';
  if (typeof window === 'undefined') return RELATIVE;
  try {
    const host = new URLSearchParams(window.location.search).get('host');
    if (host && /^https?:\/\//.test(host)) return `${host.replace(/\/$/, '')}${RELATIVE}`;
  } catch { /* noop */ }
  return RELATIVE;
}

interface AuthStatus { claude: boolean; codex: boolean; gemini: boolean }

export function ProviderConnections() {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [connecting, setConnecting] = useState<ProviderId | null>(null);

  const refreshStatus = useCallback(async () => {
    try {
      const r = await fetch(`${loomApi()}/xoro/agents/auth/status`, { credentials: 'include' });
      if (r.ok) setStatus(await r.json());
    } catch { /* noop */ }
  }, []);

  useEffect(() => { void refreshStatus(); }, [refreshStatus]);

  return (
    <>
      <div className="space-y-2">
        {PROVIDERS.map((p) => {
          const connected = status?.[p.id] ?? false;
          return (
            <div
              key={p.id}
              className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2.5"
            >
              <Plug className="h-3.5 w-3.5 text-fg-muted" />
              <span className="text-[13px] font-medium text-fg">{p.label}</span>
              {connected ? (
                <span className="rounded bg-success/10 px-1.5 py-0.5 text-2xs font-medium text-success">
                  Connected
                </span>
              ) : (
                <span className="rounded bg-surface-2 px-1.5 py-0.5 text-2xs font-medium text-fg-subtle">
                  Not connected
                </span>
              )}
              <button
                type="button"
                onClick={() => setConnecting(p.id)}
                className="focus-ring ml-auto rounded-md border border-border bg-canvas px-2.5 py-1 text-2xs font-medium text-fg hover:bg-surface-2"
              >
                {connected ? 'Reconnect' : 'Connect'}
              </button>
            </div>
          );
        })}
      </div>

      {connecting && (
        <ConnectModal
          provider={connecting}
          onClose={() => { setConnecting(null); void refreshStatus(); }}
        />
      )}

      <LocalhostPreviewSection />
    </>
  );
}

/**
 * "Localhost preview" — gated behind LOOM_PROXY_ENABLED on the server.
 * Hidden entirely if the probe says disabled, so a flipped flag effectively
 * removes the section from the UI.
 */
function LocalhostPreviewSection() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [target, setTarget] = useState('localhost:3000');
  const activeSession = useSessionStore((s) => s.sessions.find((x) => x.id === s.activeId));
  const setPanelState = useSessionStore((s) => s.setPanelState);
  const setInspectorOpen = useUIStore((s) => s.setInspectorOpen);
  const setMode = useUIStore((s) => s.setMode);

  useEffect(() => {
    fetch(`${loomApi()}/xoro/proxy-status`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((d: { enabled: boolean }) => setEnabled(d.enabled))
      .catch(() => setEnabled(false));
  }, []);

  if (enabled === null || enabled === false) return null;

  const submit = () => {
    const cleaned = target.trim().replace(/^https?:\/\//, '');
    if (!cleaned) return;
    if (!activeSession) {
      alert('Open a session first so the preview has somewhere to render.');
      return;
    }
    // Loom serves Xoro at the same origin, so a relative URL stays inside
    // the iframe's parent origin and the proxy guard catches the rest.
    const url = `${window.location.origin}/api/v1/xoro/proxy/${cleaned.replace(/^\/+/, '')}/`;
    setPanelState(activeSession.id, { preview: { url, title: cleaned } });
    setMode('preview');
    setInspectorOpen(true);
  };

  return (
    <>
      <div className="mb-2 mt-6 flex items-center gap-1.5 text-2xs uppercase tracking-wide text-fg-subtle">
        <Globe className="h-3 w-3" />
        <span>Localhost preview</span>
      </div>
      <div className="rounded-md border border-border bg-surface px-3 py-2.5">
        <label className="mb-1.5 block text-2xs uppercase tracking-wide text-fg-subtle">
          Target host:port
        </label>
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            placeholder="localhost:3000  ·  100.125.230.110:5173  ·  yourbox.tail2619af.ts.net:8080"
            className="focus-ring flex-1 rounded-md border border-border bg-canvas px-2.5 py-1.5 text-[12.5px] text-fg placeholder:text-fg-subtle focus:border-border-strong"
          />
          <button
            type="button"
            onClick={submit}
            className="focus-ring rounded-md bg-fg px-3 py-1.5 text-[12.5px] font-medium text-canvas hover:bg-fg/90"
          >
            Open in preview
          </button>
        </div>
        <p className="mt-2 text-[11.5px] leading-relaxed text-fg-muted">
          Loom proxies the target into the same origin so select-element works
          on real dev servers. The VPS must reach the host — bind your dev
          server to <code className="font-mono">0.0.0.0</code> and use a
          reachable IP (RFC 1918, Tailscale CGNAT, or Tailnet hostname).
          HMR (WebSocket) is not proxied; refresh manually after edits.
        </p>
      </div>
    </>
  );
}

interface ConnectModalProps { provider: ProviderId; onClose: () => void }

function ConnectModal({ provider, onClose }: ConnectModalProps) {
  const [handle, setHandle] = useState<string | null>(null);
  const [output, setOutput] = useState('');
  const [exited, setExited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingLine, setPendingLine] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const esRef = useRef<EventSource | null>(null);
  const outRef = useRef<HTMLDivElement | null>(null);

  // Boot the auth flow on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${loomApi()}/xoro/agents/auth/start`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ provider }),
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.error ?? `start ${r.status}`);
        }
        const data = (await r.json()) as { handle: string };
        if (cancelled) return;
        setHandle(data.handle);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [provider]);

  // Open SSE stream once we have a handle.
  useEffect(() => {
    if (!handle) return;
    const es = new EventSource(
      `${loomApi()}/xoro/agents/auth/stream?handle=${encodeURIComponent(handle)}`,
      { withCredentials: true },
    );
    esRef.current = es;
    es.addEventListener('data', (ev) => {
      try {
        const { chunk } = JSON.parse((ev as MessageEvent).data);
        setOutput((o) => stripAnsi(o + chunk));
      } catch { /* noop */ }
    });
    es.addEventListener('exit', () => {
      setExited(true);
      es.close();
    });
    es.addEventListener('error', () => { /* SSE auto-retries */ });
    return () => { es.close(); };
  }, [handle]);

  // Auto-scroll output box.
  useEffect(() => {
    if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
  }, [output]);

  // Pull the first http(s) URL out of the buffered output so the UI can
  // surface a Copy button — most CLIs print a single auth URL, but if
  // multiple are printed we just grab the first.
  const detectedUrl = output.match(/https?:\/\/[^\s)>"']+/)?.[0] ?? null;

  const submitLine = async () => {
    if (!handle || !pendingLine.trim()) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${loomApi()}/xoro/agents/auth/respond`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ handle, line: pendingLine }),
      });
      if (!r.ok) throw new Error(`respond ${r.status}`);
      setPendingLine('');
    } catch (e) {
      setError((e as Error).message ?? String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async () => {
    if (handle) {
      try {
        await fetch(`${loomApi()}/xoro/agents/auth/respond`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ handle, abort: true }),
        });
      } catch { /* noop */ }
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/15 animate-fade-in">
      <div className="w-[min(540px,calc(100vw-32px))] rounded-xl border border-border bg-canvas shadow-menu animate-fade-in">
        <div className="flex items-center gap-2 border-b border-border px-5 py-3">
          <Plug className="h-4 w-4 text-fg-muted" />
          <h3 className="text-[14px] font-semibold text-fg">
            Connect {provider === 'claude' ? 'Claude' : provider === 'codex' ? 'Codex' : 'Gemini'}
          </h3>
          <button onClick={cancel} className="focus-ring ml-auto rounded p-1 text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          {error && (
            <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-[12px] text-danger">
              {error}
            </div>
          )}

          {detectedUrl && !exited && (
            <UrlBlock url={detectedUrl} />
          )}

          {!exited && handle && (
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-2xs uppercase tracking-wide text-fg-subtle">
                <RefreshCw className="h-3 w-3" />
                Paste verification code (or any line the CLI is asking for)
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={pendingLine}
                  onChange={(e) => setPendingLine(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void submitLine(); }}
                  placeholder="Paste here, then press Enter"
                  className="focus-ring flex-1 rounded-md border border-border bg-surface px-2.5 py-1.5 text-[13px] text-fg placeholder:text-fg-subtle focus:border-border-strong"
                />
                <button
                  type="button"
                  onClick={submitLine}
                  disabled={!pendingLine.trim() || submitting}
                  className="focus-ring rounded-md bg-fg px-3 py-1.5 text-[12.5px] font-medium text-canvas hover:bg-fg/90 disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Submit'}
                </button>
              </div>
            </div>
          )}

          {exited && (
            <div className="flex items-center gap-2 rounded-md border border-success/40 bg-success/5 px-3 py-2 text-[12px] text-success">
              <Check className="h-4 w-4" />
              Auth flow finished. Check status — close this dialog when ready.
            </div>
          )}

          <details className="rounded-md border border-border bg-surface">
            <summary className="cursor-pointer px-3 py-2 text-2xs uppercase tracking-wide text-fg-subtle">
              <TerminalIcon className="mr-1.5 inline h-3 w-3" />
              CLI output
            </summary>
            <div
              ref={outRef}
              className="max-h-[180px] overflow-auto whitespace-pre-wrap break-words border-t border-border bg-canvas px-3 py-2 font-mono text-[11px] text-fg-muted"
            >
              {output || (handle ? 'Waiting for output…' : 'Starting…')}
            </div>
          </details>
        </div>

        <div className="flex items-center gap-2 border-t border-border px-5 py-3">
          <span className="text-2xs text-fg-subtle">
            Status refreshes when you close this dialog.
          </span>
          <button
            onClick={onClose}
            className="focus-ring ml-auto rounded-md bg-fg px-3 py-1.5 text-[12.5px] font-medium text-canvas hover:bg-fg/90"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function UrlBlock({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-md border border-accent/40 bg-accent/5 px-3 py-2.5">
      <div className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-accent">
        Open this URL in your browser
      </div>
      <div className="flex items-center gap-1.5">
        <input
          readOnly
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          className="flex-1 truncate rounded-md border border-border bg-canvas px-2 py-1 font-mono text-[11.5px] text-fg"
        />
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(url);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch { /* noop */ }
          }}
          className="focus-ring rounded-md border border-border bg-surface px-2 py-1 text-2xs font-medium text-fg hover:bg-surface-2"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="focus-ring rounded-md border border-border bg-surface px-2 py-1 text-2xs font-medium text-fg hover:bg-surface-2"
          aria-label="Open in new tab"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <div className="mt-1.5 text-2xs text-fg-muted">
        Sign in, then paste the verification code below.
      </div>
    </div>
  );
}

/** Strip ANSI escape sequences so the CLI output is readable in plain HTML. */
function stripAnsi(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/\x1b\[[0-9;?]*[ -/]*[@-~]/g, '');
}
